import { Alert, Button, Modal, Space, Spin, Typography, message } from "antd";
import { MobileOutlined } from "@ant-design/icons";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SignatureCanvas from "react-signature-canvas";
import ReactSignatureCanvas from "react-signature-canvas";
import { NCALayerClient } from "ncalayer-js-client";
import { ApiService } from "../api";
import EgovMobileSignModal from "./EgovMobileSignModal";

const { Text } = Typography;

type Props = {
  open: boolean;
  onCancel: () => void;
  // Receives the signature blob; resolves once the section is submitted.
  onSigned: (sign: string) => Promise<void>;
  requestId?: string;
  sectionTitle: string;
  // 'Drawn' -> signature pad, 'EDS' -> NCALayer. Anything else never opens this.
  kind: string;
};

/**
 * Signing one section of a permit.
 *
 * The paper form has a signature line under each block, not just at the end, so
 * a section can be configured to require its own signature before it can be
 * submitted. This is where that signature is taken: drawn on a pad, or — for an
 * EDS section — produced over the permit document by either of the two routes
 * the approval steps already offer, NCALayer on the desktop or eGov Mobile by
 * QR. What the certificate attests to is the permit document (that is what an
 * EDS can be issued over); which *block* it was given for is what this section
 * records.
 */
const SectionSignModal = ({
  open,
  onCancel,
  onSigned,
  requestId,
  sectionTitle,
  kind,
}: Props) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [egovOpen, setEgovOpen] = useState(false);
  const sigCanvas = useRef<ReactSignatureCanvas>(null);

  const handleDrawn = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      message.warning(t("Please sign before submitting"));
      return;
    }
    setBusy(true);
    try {
      await onSigned(
        sigCanvas.current.getTrimmedCanvas().toDataURL("image/png")
      );
    } finally {
      setBusy(false);
    }
  };

  const handleEds = async () => {
    const client = new NCALayerClient();
    setBusy(true);
    try {
      await client.connect();
    } catch (error: any) {
      setBusy(false);
      message.error(`${t("Failed to connect to NCALayer:")} ${error.toString()}`);
      return;
    }

    try {
      const fileData = await ApiService.apiMainGetRequestDocRetrieve(
        parseInt(requestId!)
      );
      const signature = await client.basicsSignCMS(
        NCALayerClient.basicsStoragesAll,
        fileData,
        NCALayerClient.basicsCMSParamsAttached,
        NCALayerClient.basicsSignerSignAny
      );
      await onSigned(
        Array.isArray(signature) ? signature[0] : (signature as string)
      );
    } catch (error: any) {
      if (error?.canceledByUser) {
        message.info(t("Action cancelled by user."));
      } else {
        message.error(error?.toString?.() ?? t("Something went wrong"));
      }
    } finally {
      setBusy(false);
    }
  };

  const isDrawn = kind === "Drawn";

  return (
    <Modal
      title={`${t("Sign")}: ${t(sectionTitle)}`}
      open={open}
      onCancel={onCancel}
      confirmLoading={busy}
      footer={
        isDrawn
          ? [
              <Button
                key="clear"
                disabled={busy}
                onClick={() => sigCanvas.current?.clear()}
              >
                {t("Clear")}
              </Button>,
              <Button key="cancel" disabled={busy} onClick={onCancel}>
                {t("Cancel")}
              </Button>,
              <Button
                key="ok"
                type="primary"
                loading={busy}
                onClick={handleDrawn}
              >
                {t("Sign and submit")}
              </Button>,
            ]
          : [
              <Button key="cancel" disabled={busy} onClick={onCancel}>
                {t("Cancel")}
              </Button>,
              <Button
                key="egov"
                icon={<MobileOutlined />}
                disabled={busy}
                onClick={() => setEgovOpen(true)}
              >
                {t("Sign with eGov Mobile")}
              </Button>,
              <Button key="ok" type="primary" loading={busy} onClick={handleEds}>
                {t("Sign by EDS")}
              </Button>,
            ]
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t(
          "Your signature applies to this section only. Editing it afterwards withdraws the signature."
        )}
      />
      {isDrawn ? (
        <SignatureCanvas
          ref={sigCanvas}
          penColor="black"
          canvasProps={{
            width: 468,
            height: 200,
            className: "sigCanvas",
            style: {
              border: "0.5px solid grey",
              borderRadius: "5px",
            },
          }}
        />
      ) : (
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", padding: "12px 0" }}
        >
          {busy ? <Spin /> : null}
          <Text type="secondary" style={{ textAlign: "center" }}>
            {t(
              "Sign the permit document with NCALayer on this computer, or with eGov Mobile by QR code."
            )}
          </Text>
        </Space>
      )}

      {/* The QR route: SIGEX relays the same permit document to eGov Mobile and
          returns the CMS, which is submitted through this section's endpoint. */}
      <EgovMobileSignModal
        requestId={requestId}
        documentLabel={`${t("Permit to Work document")} — ${t(sectionTitle)}`}
        visible={egovOpen}
        setVisible={setEgovOpen}
        onSigned={async (sign) => {
          await onSigned(sign);
          setEgovOpen(false);
        }}
      />
    </Modal>
  );
};

export default SectionSignModal;
